import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "../api/client";

interface SavedSearch {
  id: string;
  source: "zillow" | "realtor";
  name: string;
  url: string;
  last_run_at: string | null;
  last_count: number | null;
}

interface AdditionalFilters {
  min_sqft: number | null;
  max_price: number | null;
  target_towns: string[];
  min_school_score: number | null;
}

interface TownRating {
  town: string;
  rating: number; // 0-10
}

interface ScoringWeights {
  town: number;
  sqft: number;
  schools: number;
  value: number;
  noise: number;
  amenities: number;
  garage: number;
  lot_size: number;
  year_built: number;
  beds: number;
  baths: number;
}

interface ScoringPreferences {
  weights: ScoringWeights;
  town_ratings: TownRating[];
  target_sqft: number | null;
  target_beds: number | null;
  target_baths: number | null;
  target_lot_size: number | null;
  min_year_built: number | null;
  target_garage_spaces: number | null;
  desired_amenities: string[];
}

interface Config {
  saved_searches: SavedSearch[];
  additional_filters: AdditionalFilters;
  scoring_preferences: ScoringPreferences;
  ai_analysis: { model: string };
}

interface AnalysisModel {
  id: string;
  label: string;
  provider: string;
  route: string;
}

type SettingsTab = "general" | "scoring";

export default function Settings() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<SettingsTab>(params.get("tab") === "scoring" ? "scoring" : "general");
  return (
    <>
      <h1 style={{ marginTop: 0 }}>Settings</h1>
      <div className="row" style={{ gap: 0, marginBottom: 16, borderBottom: "1px solid var(--border)" }}>
        <TabButton active={tab === "general"} onClick={() => setTab("general")}>
          General
        </TabButton>
        <TabButton active={tab === "scoring"} onClick={() => setTab("scoring")}>
          Match scoring
        </TabButton>
      </div>
      {tab === "general" && (
        <>
          <SavedSearchesSection />
          <AdditionalFiltersSection />
          <AnalysisModelSection />
          <CountyRecordsSection />
          <StreetNoiseSection />
          <ExtensionKeySection />
        </>
      )}
      {tab === "scoring" && (
        <>
          <MatchScoringSection />
          <LearnedPreferencesSection />
        </>
      )}
    </>
  );
}

interface PreferenceSuggestion {
  rule: string;
  evidence: string;
}

/** Rules learned from the visit log. Suggestions are only proposals: nothing
 *  reaches the verdict or chat until it's in the saved list below. */
function LearnedPreferencesSection() {
  const qc = useQueryClient();
  const saved = useQuery<{ rules: string[]; updated_at: string | null }>({
    queryKey: ["learned-preferences"],
    queryFn: () => apiGet("/api/preferences/learned"),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<PreferenceSuggestion[] | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const suggest = useMutation({
    mutationFn: () =>
      apiPost<{ suggestions: PreferenceSuggestion[]; visit_count: number }>(
        "/api/preferences/learned/suggest", {},
      ),
    onSuccess: (data) => {
      setSuggestions(data.suggestions);
      setPicked(new Set(data.suggestions.map((_, i) => i)));
    },
  });
  const save = useMutation({
    mutationFn: (rules: string[]) => apiPut("/api/preferences/learned", { rules }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["learned-preferences"] });
      setDraft(null);
    },
  });

  const savedText = (saved.data?.rules ?? []).join("\n");
  const text = draft ?? savedText;
  const rules = text.split("\n").map((r) => r.trim()).filter(Boolean);

  function addPicked() {
    const merged = [...rules];
    suggestions?.forEach((s, i) => {
      if (picked.has(i) && !merged.includes(s.rule)) merged.push(s.rule);
    });
    setDraft(merged.join("\n"));
    setSuggestions(null);
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Learned from your visits</h3>
      <p className="muted">
        Preferences the search form can't express — room sizes, layouts, upkeep you won't pay
        for. Suggest rules from your visit notes and quick answers, keep the ones that are
        right, and save. Saved rules shape the AI verdict and chat answers; they never change
        the market valuation or the match score.
      </p>
      <button onClick={() => suggest.mutate()} disabled={suggest.isPending}>
        {suggest.isPending ? "Reading your visits…" : "Suggest from my visits"}
      </button>
      {suggest.isError && <p role="alert">{suggest.error.message}</p>}

      {suggestions && (
        <div style={{ margin: "12px 0", border: "1px solid var(--border)", borderRadius: 6, padding: 12 }}>
          <strong style={{ fontSize: 13 }}>Suggestions — tick the ones that are right</strong>
          {suggestions.length === 0 && <p className="muted">No clear patterns yet.</p>}
          {suggestions.map((s, i) => (
            <label key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10 }}>
              <input
                type="checkbox"
                checked={picked.has(i)}
                onChange={() =>
                  setPicked((prev) => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    return next;
                  })
                }
              />
              <span>
                {s.rule}
                {s.evidence && (
                  <span className="muted" style={{ display: "block", fontSize: 12 }}>
                    From: {s.evidence}
                  </span>
                )}
              </span>
            </label>
          ))}
          <div className="row" style={{ marginTop: 12 }}>
            <button onClick={addPicked} disabled={picked.size === 0}>
              Add {picked.size} to the list
            </button>
            <button
              onClick={() => setSuggestions(null)}
              style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--muted)" }}
            >
              Discard
            </button>
          </div>
        </div>
      )}

      <label style={{ display: "block", marginTop: 12 }}>
        <span className="muted" style={{ fontSize: 11 }}>Saved rules — one per line</span>
        <textarea
          rows={Math.max(4, rules.length + 1)}
          value={text}
          onChange={(e) => {
            setDraft(e.target.value);
            save.reset();
          }}
          placeholder="Secondary bedrooms must fit a bed and a dresser"
          style={{ width: "100%" }}
        />
      </label>
      <div className="row" style={{ marginTop: 8 }}>
        <button onClick={() => save.mutate(rules)} disabled={draft === null || save.isPending}>
          {save.isPending ? "Saving…" : "Save rules"}
        </button>
        {draft !== null && (
          <button
            onClick={() => setDraft(null)}
            style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--muted)" }}
          >
            Revert
          </button>
        )}
        {saved.data?.updated_at && draft === null && (
          <span className="muted" style={{ fontSize: 12 }}>
            {rules.length} rule{rules.length === 1 ? "" : "s"} in use
          </span>
        )}
      </div>
      {save.isError && <p role="alert">Could not save: {save.error.message}</p>}
      {saved.isError && <p role="alert">Unable to load saved rules: {saved.error.message}</p>}
    </div>
  );
}

function AnalysisModelSection() {
  const qc = useQueryClient();
  const config = useQuery<Config>({
    queryKey: ["config"], queryFn: () => apiGet("/api/config"),
  });
  const catalog = useQuery<{ models: AnalysisModel[]; region: string }>({
    queryKey: ["analysis-models"], queryFn: () => apiGet("/api/analysis/models"),
  });
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (model: string) => apiPut("/api/config/ai-analysis", { model }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["config"] });
      setDraft(null);
    },
  });
  const current = config.data?.ai_analysis?.model ?? "";
  const selected = draft ?? current;
  const models = catalog.data?.models ?? [];
  const listed = models.some((model) => model.id === selected);
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Analysis and chatbot model</h3>
      <p className="muted">
        Choose a Bedrock model for photo review, property valuation, and the chatbot.
        Changes apply to the next analysis or chat message; existing reports stay as
        they are until regenerated. Chat models without tool support can answer from
        page context but cannot search listings or change map filters.
      </p>
      <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
        <label htmlFor="analysis-model">Model</label>
        <select
          id="analysis-model"
          value={selected}
          disabled={!config.data || catalog.isPending || save.isPending || models.length === 0}
          onChange={(event) => { setDraft(event.target.value); save.reset(); }}
          style={{ flex: 1, minWidth: 200, maxWidth: "100%" }}
        >
          {!listed && <option value={selected}>{selected || "Loading…"} (current)</option>}
          {models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.provider} · {model.label} · {model.route}
            </option>
          ))}
        </select>
        <button
          onClick={() => save.mutate(selected)}
          disabled={!listed || selected === current || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save model"}
        </button>
        <button onClick={() => { void catalog.refetch(); }} disabled={catalog.isFetching}>
          {catalog.isFetching ? "Refreshing…" : "Refresh models"}
        </button>
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        The list comes from Bedrock’s current text-and-image catalog
        {catalog.data?.region ? ` in ${catalog.data.region}` : ""}.
        Model access and capabilities depend on your AWS account.
        {" "}<a href="https://aws.amazon.com/bedrock/pricing/" target="_blank" rel="noreferrer">
          Compare Bedrock pricing ↗
        </a>
      </p>
      <p className="muted" style={{ fontSize: 12 }}>
        Global routes can cost less than US routes, but may process requests outside the US.
        Compare the rates for your chosen model and route.
      </p>
      {catalog.isError && <p role="alert">{catalog.error.message}</p>}
      {config.isError && <p role="alert">Unable to load the saved model: {config.error.message}</p>}
      {catalog.isSuccess && models.length === 0 && <p role="status">No active vision models were returned by Bedrock.</p>}
      {save.isError && <p role="alert">Could not save model: {save.error.message}</p>}
      {save.isSuccess && <p role="status">Model saved.</p>}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
        borderRadius: 0,
        color: active ? "var(--text)" : "var(--muted)",
        fontWeight: active ? 600 : 400,
        padding: "8px 16px",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function ExtensionInstallHint() {
  return (
    <details>
      <summary className="muted" style={{ cursor: "pointer" }}>
        Extension install instructions
      </summary>
      <ol>
        <li>Clone the repo and run <code>cd extension/</code>.</li>
        <li>Open <code>chrome://extensions</code> in Chrome (or Edge).</li>
        <li>Toggle "Developer mode" on (top right).</li>
        <li>Click "Load unpacked" and choose the <code>extension/</code> folder.</li>
        <li>
          Open the extension popup, paste the API URL and the extension API key (generated below).
        </li>
        <li>
          Sign in to Zillow / Realtor in another tab, then click a Run sweep button below.
        </li>
      </ol>
    </details>
  );
}

function SavedSearchesSection() {
  const qc = useQueryClient();
  const q = useQuery<Config>({
    queryKey: ["config"],
    queryFn: () => apiGet("/api/config"),
  });
  const mut = useMutation({
    mutationFn: (next: Config) => apiPut("/api/config", next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["config"] }),
  });

  const [draftName, setDraftName] = useState("");
  const [draftUrl, setDraftUrl] = useState("");
  const [draftSource, setDraftSource] = useState<"zillow" | "realtor">("zillow");

  if (!q.data) return <div className="panel">Loading saved searches…</div>;
  const list = q.data.saved_searches ?? [];

  function add() {
    if (!draftName.trim() || !draftUrl.trim()) return;
    const next: SavedSearch = {
      id: crypto.randomUUID(),
      source: draftSource,
      name: draftName.trim(),
      url: draftUrl.trim(),
      last_run_at: null,
      last_count: null,
    };
    mut.mutate({ ...q.data!, saved_searches: [...list, next] });
    setDraftName("");
    setDraftUrl("");
  }

  function remove(id: string) {
    mut.mutate({ ...q.data!, saved_searches: list.filter((s) => s.id !== id) });
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Saved searches</h3>
      <table>
        <thead>
          <tr>
            <th>Source</th>
            <th>Name</th>
            <th>URL</th>
            <th>Last run</th>
            <th>Count</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {list.map((s) => (
            <tr key={s.id}>
              <td>{s.source}</td>
              <td>{s.name}</td>
              <td style={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
                <a href={s.url} target="_blank" rel="noreferrer">
                  {s.url}
                </a>
              </td>
              <td className="muted">{s.last_run_at ?? "—"}</td>
              <td>{s.last_count ?? "—"}</td>
              <td>
                <button onClick={() => remove(s.id)}>Delete</button>
              </td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr>
              <td colSpan={6} className="muted">
                No saved searches yet. Add the URL of a Zillow or Realtor.com saved search below.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
        Tip: to import your favorited listings, navigate to your "Saved Homes"
        page on Zillow / Realtor while signed in, copy the URL from the
        address bar, and add it here as a saved search. The sweep treats it
        like any other search and will pull in the listings you've favorited.
      </p>
      <div className="row" style={{ marginTop: 12 }}>
        <select value={draftSource} onChange={(e) => setDraftSource(e.target.value as "zillow" | "realtor")}>
          <option value="zillow">zillow</option>
          <option value="realtor">realtor</option>
        </select>
        <input
          placeholder="Friendly name (e.g. Middletown 4BR)"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          style={{ width: 240 }}
        />
        <input
          placeholder="Saved-search URL"
          value={draftUrl}
          onChange={(e) => setDraftUrl(e.target.value)}
          style={{ flex: 1, minWidth: 300 }}
        />
        <button onClick={add} disabled={mut.isPending}>
          Add
        </button>
      </div>
      <RunSweepRow savedSearches={list} />
      <ExtensionInstallHint />
    </div>
  );
}

function RunSweepRow({ savedSearches }: { savedSearches: SavedSearch[] }) {
  const [running, setRunning] = useState<"zillow" | "realtor" | null>(null);
  const [progressLines, setProgressLines] = useState<string[]>([]);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "MA_RUN_SWEEP_PROGRESS") {
        setProgressLines((prev) => [...prev, d.text].slice(-30));
      }
      if (d.type === "MA_RUN_SWEEP_RESULT") {
        const r = d.result;
        if (r?.ok) {
          setResult(`✓ ${d.source}: ${r.countNew} new, ${r.countUpdated} updated`);
        } else {
          setResult(`✗ ${d.source}: ${r?.error || "failed"}`);
        }
        setRunning(null);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  function run(source: "zillow" | "realtor") {
    setRunning(source);
    setProgressLines([]);
    setResult(null);
    window.postMessage(
      { type: "MA_RUN_SWEEP", source, savedSearches },
      window.location.origin,
    );
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
      <div className="row">
        <span className="muted" style={{ fontSize: 12 }}>
          Sweeps run from your browser using your logged-in Zillow / Realtor sessions:
        </span>
        <span style={{ flex: 1 }} />
        <button onClick={() => run("zillow")} disabled={running !== null}>
          {running === "zillow" ? "Running…" : "Run Zillow sweep"}
        </button>
        <button onClick={() => run("realtor")} disabled={running !== null}>
          {running === "realtor" ? "Running…" : "Run Realtor sweep"}
        </button>
      </div>
      {(progressLines.length > 0 || result) && (
        <pre
          style={{
            marginTop: 10,
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: 8,
            fontSize: 11,
            maxHeight: 200,
            overflowY: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          {progressLines.join("\n")}
          {result ? `\n${result}` : ""}
        </pre>
      )}
    </div>
  );
}

function AdditionalFiltersSection() {
  const qc = useQueryClient();
  const q = useQuery<Config>({
    queryKey: ["config"],
    queryFn: () => apiGet("/api/config"),
  });
  const mut = useMutation({
    mutationFn: (next: Config) => apiPut("/api/config", next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["config"] }),
  });

  if (!q.data) return null;
  const f = q.data.additional_filters;

  function update<K extends keyof AdditionalFilters>(key: K, value: AdditionalFilters[K]) {
    mut.mutate({
      ...q.data!,
      additional_filters: { ...f, [key]: value },
    });
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Additional filters</h3>
      <p className="muted">Applied on top of saved-search results, in case Zillow's filters are too loose.</p>
      <div className="row">
        <label>
          Min sqft{" "}
          <input
            type="number"
            value={f.min_sqft ?? ""}
            onChange={(e) => update("min_sqft", e.target.value ? Number(e.target.value) : null)}
            style={{ width: 120 }}
          />
        </label>
        <label>
          Max price{" "}
          <input
            type="number"
            value={f.max_price ?? ""}
            onChange={(e) => update("max_price", e.target.value ? Number(e.target.value) : null)}
            style={{ width: 160 }}
          />
        </label>
        <label>
          Min school score{" "}
          <input
            type="number"
            min={1}
            max={10}
            value={f.min_school_score ?? ""}
            onChange={(e) =>
              update("min_school_score", e.target.value ? Number(e.target.value) : null)
            }
            style={{ width: 80 }}
          />
        </label>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <label>
          Target towns (comma-separated){" "}
          <input
            type="text"
            value={f.target_towns.join(", ")}
            onChange={(e) =>
              update(
                "target_towns",
                e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              )
            }
            style={{ width: 400 }}
          />
        </label>
      </div>
    </div>
  );
}

const WEIGHT_LABELS: Record<keyof ScoringWeights, { label: string; hint: string }> = {
  town: { label: "Town", hint: "How much the town rating below matters" },
  sqft: { label: "Square footage", hint: "Closeness to your target sqft" },
  schools: { label: "Schools", hint: "Average of the listing's school scores" },
  value: { label: "Value", hint: "List price vs. the AI fair-price estimate" },
  noise: {
    label: "Street noise",
    hint: "How quiet the address is and how calm the street it fronts onto is",
  },
  amenities: { label: "Amenities", hint: "Fraction of your must-have amenities the listing has" },
  garage: { label: "Garage", hint: "Closeness to your target garage spaces" },
  lot_size: { label: "Lot size", hint: "Closeness to your target lot size" },
  year_built: { label: "Year built", hint: "Penalizes homes older than your cutoff" },
  beds: { label: "Bedrooms", hint: "Closeness to your target bed count" },
  baths: { label: "Bathrooms", hint: "Closeness to your target bath count" },
};

// Mirrors backend lib/amenities.AMENITY_KEYS (minus has_basement, which is
// subsumed by finished_basement for preference purposes).
const AMENITY_OPTIONS: { key: string; label: string }[] = [
  { key: "fenced_yard", label: "Fenced yard" },
  { key: "central_air", label: "Central air" },
  { key: "finished_basement", label: "Finished basement" },
  { key: "has_basement", label: "Basement (any)" },
  { key: "fireplace", label: "Fireplace" },
  { key: "deck_patio", label: "Deck / patio" },
  { key: "pool", label: "Pool" },
  { key: "spa", label: "Spa / hot tub" },
  { key: "attached_garage", label: "Attached garage" },
  { key: "waterfront", label: "Waterfront" },
  { key: "view", label: "View" },
];

function MatchScoringSection() {
  const qc = useQueryClient();
  const q = useQuery<Config>({
    queryKey: ["config"],
    queryFn: () => apiGet("/api/config"),
  });
  const mut = useMutation({
    mutationFn: (prefs: ScoringPreferences) =>
      apiPut("/api/config", { scoring_preferences: prefs }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["config"] }),
  });

  // Towns that actually appear in listing data, so ratings can be picked
  // (not typed) — a typo'd town would silently score its listings 0.
  const listingsQ = useQuery<{ listings: { town?: string }[] }>({
    queryKey: ["listings", "all"],
    queryFn: () => apiGet("/api/listings?status=all"),
  });

  const [draft, setDraft] = useState<ScoringPreferences | null>(null);
  const [draftTown, setDraftTown] = useState("");

  useEffect(() => {
    if (q.data && draft === null) setDraft(q.data.scoring_preferences);
  }, [q.data, draft]);

  if (!q.data || !draft) return <div className="panel">Loading scoring preferences…</div>;

  const saved = q.data.scoring_preferences;
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const knownTowns = Array.from(
    new Set([
      ...(listingsQ.data?.listings ?? []).map((l) => (l.town ?? "").trim()).filter(Boolean),
      ...(q.data.additional_filters.target_towns ?? []),
    ]),
  ).sort();
  const isRated = (town: string) =>
    draft.town_ratings.some((r) => r.town.toLowerCase() === town.toLowerCase());
  const unratedKnownTowns = knownTowns.filter((t) => !isRated(t));
  const listingTownsLower = new Set(
    (listingsQ.data?.listings ?? []).map((l) => (l.town ?? "").trim().toLowerCase()).filter(Boolean),
  );

  function setWeight(key: keyof ScoringWeights, value: number) {
    setDraft((d) => d && { ...d, weights: { ...d.weights, [key]: value } });
  }

  function setTarget<K extends keyof ScoringPreferences>(key: K, value: ScoringPreferences[K]) {
    setDraft((d) => d && { ...d, [key]: value });
  }

  function addTown(name: string) {
    const typed = name.trim();
    if (!typed || isRated(typed)) return;
    // Snap to the spelling used in listing data so the match is guaranteed.
    const town = knownTowns.find((t) => t.toLowerCase() === typed.toLowerCase()) ?? typed;
    setDraft((d) => d && { ...d, town_ratings: [...d.town_ratings, { town, rating: 5 }] });
    setDraftTown("");
  }

  function setTownRating(town: string, rating: number) {
    setDraft(
      (d) =>
        d && {
          ...d,
          town_ratings: d.town_ratings.map((r) => (r.town === town ? { ...r, rating } : r)),
        },
    );
  }

  function removeTown(town: string) {
    setDraft((d) => d && { ...d, town_ratings: d.town_ratings.filter((r) => r.town !== town) });
  }

  return (
    <>
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Factor weights</h3>
        <p className="muted">
          How much each factor contributes to a listing's match score (0 = ignored, 10 = most
          important). Factors a listing has no data for are skipped, not penalized.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(280px, 1fr))", gap: "10px 32px" }}>
          {(Object.keys(WEIGHT_LABELS) as (keyof ScoringWeights)[]).map((key) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 130, flexShrink: 0 }} title={WEIGHT_LABELS[key].hint}>
                {WEIGHT_LABELS[key].label}
              </span>
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={draft.weights[key]}
                onChange={(e) => setWeight(key, Number(e.target.value))}
                style={{ flex: 1 }}
              />
              <span style={{ width: 20, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {draft.weights[key]}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Town ratings</h3>
        <p className="muted">
          Rate each town 0–10. Listings in towns you haven't rated score 0 on the town factor.
        </p>
        {draft.town_ratings.map((r) => (
          <div key={r.town} className="row" style={{ marginBottom: 8 }}>
            <span style={{ width: 130, flexShrink: 0 }}>
              {r.town}
              {listingsQ.data && !listingTownsLower.has(r.town.trim().toLowerCase()) && (
                <span
                  title="No listings have this town name — check the spelling. Listings in unrated towns score 0 on the town factor."
                  style={{ color: "var(--bad)", marginLeft: 4, cursor: "help" }}
                >
                  ⚠
                </span>
              )}
            </span>
            <input
              type="range"
              min={0}
              max={10}
              step={1}
              value={r.rating}
              onChange={(e) => setTownRating(r.town, Number(e.target.value))}
              style={{ width: 220 }}
            />
            <span style={{ width: 20, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {r.rating}
            </span>
            <button onClick={() => removeTown(r.town)}>Remove</button>
          </div>
        ))}
        {draft.town_ratings.length === 0 && (
          <p className="muted" style={{ fontSize: 12 }}>
            No towns rated yet — the town factor is skipped until you add one.
          </p>
        )}
        <div className="row" style={{ marginTop: 8 }}>
          <input
            placeholder="Town name"
            list="known-towns"
            value={draftTown}
            onChange={(e) => setDraftTown(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTown(draftTown)}
            style={{ width: 220 }}
          />
          <datalist id="known-towns">
            {unratedKnownTowns.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
          <button onClick={() => addTown(draftTown)}>Add town</button>
          {unratedKnownTowns.map((t) => (
            <button
              key={t}
              onClick={() => addTown(t)}
              style={{ background: "transparent", border: "1px dashed var(--border)", color: "var(--muted)" }}
            >
              + {t}
            </button>
          ))}
        </div>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Targets</h3>
        <p className="muted">
          What "ideal" looks like. Listings score full marks at or above the target and scale down
          below it (year built penalizes older homes past the cutoff). Leave any target blank to
          drop that factor from scoring entirely — its weight is simply skipped, not counted
          against the listing.
        </p>
        <div className="row">
          <label>
            Target sqft{" "}
            <input
              type="number"
              value={draft.target_sqft ?? ""}
              onChange={(e) => setTarget("target_sqft", e.target.value ? Number(e.target.value) : null)}
              style={{ width: 110 }}
            />
          </label>
          <label>
            Target beds{" "}
            <input
              type="number"
              value={draft.target_beds ?? ""}
              onChange={(e) => setTarget("target_beds", e.target.value ? Number(e.target.value) : null)}
              style={{ width: 70 }}
            />
          </label>
          <label>
            Target baths{" "}
            <input
              type="number"
              step={0.5}
              value={draft.target_baths ?? ""}
              onChange={(e) => setTarget("target_baths", e.target.value ? Number(e.target.value) : null)}
              style={{ width: 70 }}
            />
          </label>
          <label>
            Target lot (sqft){" "}
            <input
              type="number"
              value={draft.target_lot_size ?? ""}
              onChange={(e) => setTarget("target_lot_size", e.target.value ? Number(e.target.value) : null)}
              style={{ width: 110 }}
            />
          </label>
          <label>
            Built after{" "}
            <input
              type="number"
              value={draft.min_year_built ?? ""}
              onChange={(e) => setTarget("min_year_built", e.target.value ? Number(e.target.value) : null)}
              style={{ width: 90 }}
            />
          </label>
          <label>
            Garage spaces{" "}
            <input
              type="number"
              min={0}
              value={draft.target_garage_spaces ?? ""}
              onChange={(e) =>
                setTarget("target_garage_spaces", e.target.value ? Number(e.target.value) : null)
              }
              style={{ width: 70 }}
            />
          </label>
        </div>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Must-have amenities</h3>
        <p className="muted">
          The amenities factor scores each listing by the fraction of these it has. Amenity data
          comes from Zillow's property facts (pulled during sweeps); if Zillow says nothing about
          an amenity, it's left out of that listing's calculation rather than counted against it.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(160px, 1fr))", gap: "8px 16px" }}>
          {AMENITY_OPTIONS.map((opt) => (
            <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={draft.desired_amenities.includes(opt.key)}
                onChange={(e) =>
                  setTarget(
                    "desired_amenities",
                    e.target.checked
                      ? [...draft.desired_amenities, opt.key]
                      : draft.desired_amenities.filter((k) => k !== opt.key),
                  )
                }
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div className="row" style={{ marginTop: 4 }}>
        <button
          onClick={() => mut.mutate(draft)}
          disabled={!dirty || mut.isPending}
          style={{ background: dirty ? "var(--good)" : undefined }}
        >
          {mut.isPending ? "Saving…" : "Save scoring preferences"}
        </button>
        {dirty && !mut.isPending && (
          <button
            onClick={() => setDraft(saved)}
            style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text)" }}
          >
            Discard changes
          </button>
        )}
        {!dirty && mut.isSuccess && (
          <span className="tag-active" style={{ fontSize: 12 }}>
            ✓ Saved — listings re-score in the background
          </span>
        )}
      </div>
    </>
  );
}

function CountyRecordsSection() {
  const backfill = useMutation({
    mutationFn: () =>
      apiPost<{ candidates: number; queued: number }>("/api/modiv/backfill"),
  });

  return (
    <div className="panel">
      <h2 style={{ marginTop: 0 }}>County tax records</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Monmouth County publishes the assessor's square footage, year built and acreage for every
        parcel. Zillow leaves square footage blank often enough to matter — and because an unknown
        size is penalized in match scoring, filling it in changes rankings. This queues a lookup for
        every listing that's still missing a size.
      </p>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Unlike the Zillow sweeps this runs server-side (the county site has no bot protection), so
        you don't need the extension or a browser tab open. Addresses are matched on house number
        plus street, and anything that can't be narrowed to exactly one parcel is left alone rather
        than guessed at.
      </p>
      <div className="row">
        <button onClick={() => backfill.mutate()} disabled={backfill.isPending}>
          {backfill.isPending ? "Queueing…" : "Backfill missing square footage"}
        </button>
        {backfill.isSuccess && (
          <span className="muted">
            {backfill.data.queued === 0
              ? "Nothing to do — every listing already has a size or has been checked."
              : `Queued ${backfill.data.queued} of ${backfill.data.candidates} listing(s). Results appear on each listing page.`}
          </span>
        )}
        {backfill.error && <span className="tag-error">{String(backfill.error)}</span>}
      </div>
    </div>
  );
}

function StreetNoiseSection() {
  const backfill = useMutation({
    mutationFn: () => apiPost<{ candidates: number; queued: number }>("/api/noise/backfill"),
  });

  return (
    <div className="panel">
      <h2 style={{ marginTop: 0 }}>Street noise &amp; traffic</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        A listing tells you the lot size and never mentions that the lot backs onto the Parkway.
        This scores two things from each listing's coordinates: how loud the address is, and how
        busy the street it actually fronts onto is. They come apart — a cul-de-sac behind a highway
        is loud with a dead street; a county road is the reverse — so both feed match scoring, with
        ambient noise weighted higher. Adjust or zero the weight under Scoring.
      </p>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Road geometry comes from OpenStreetMap and traffic volumes from NJDOT's published counts,
        both free and server-side, so no extension or browser tab is needed. NJDOT only counts state
        routes, so a residential street falls back to an estimate from its road class rather than
        being assumed quiet.
      </p>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Sweeps score new listings automatically, so you shouldn't normally need this. It's here to
        catch up listings that predate the feature, or to retry ones whose lookup failed. Results
        are static — roads don't move — so a scored listing is never queued twice.
      </p>
      <div className="row">
        <button onClick={() => backfill.mutate()} disabled={backfill.isPending}>
          {backfill.isPending ? "Queueing…" : "Catch up unscored listings"}
        </button>
        {backfill.isSuccess && (
          <span className="muted">
            {backfill.data.queued === 0
              ? "Nothing to do — every listing with coordinates has been scored."
              : `Queued ${backfill.data.queued} of ${backfill.data.candidates} listing(s). Results appear on each listing page.`}
          </span>
        )}
        {backfill.error && <span className="tag-error">{String(backfill.error)}</span>}
      </div>
    </div>
  );
}

function ExtensionKeySection() {
  const apiUrl = import.meta.env.VITE_API_URL;
  const qc = useQueryClient();
  const q = useQuery<{ extension_api_key: string | null }>({
    queryKey: ["extension-key"],
    queryFn: () => apiGet("/api/extension-key"),
  });
  const rotate = useMutation({
    mutationFn: () => apiPost<{ extension_api_key: string }>("/api/extension-key"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["extension-key"] }),
  });

  const currentKey = q.data?.extension_api_key ?? null;

  const [sendStatus, setSendStatus] = useState<string | null>(null);

  function sendToExtension() {
    if (!currentKey) {
      setSendStatus("Generate a key first");
      return;
    }
    setSendStatus("Sending…");
    let resolved = false;
    function onMessage(e: MessageEvent) {
      if (e.source !== window) return;
      const d = e.data;
      if (!d || d.type !== "MA_CONFIGURE_EXTENSION_RESULT") return;
      resolved = true;
      window.removeEventListener("message", onMessage);
      if (d.result?.ok) {
        setSendStatus("✓ Saved into the extension");
      } else {
        setSendStatus(`Failed: ${d.result?.error ?? "unknown"}`);
      }
    }
    window.addEventListener("message", onMessage);
    window.postMessage(
      { type: "MA_CONFIGURE_EXTENSION", apiUrl, extensionKey: currentKey },
      window.location.origin,
    );
    setTimeout(() => {
      if (!resolved) {
        window.removeEventListener("message", onMessage);
        setSendStatus("No response — is the extension installed and reloaded?");
      }
    }, 1500);
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Extension API key</h3>
      <p className="muted">
        Click <strong>Send to extension</strong> to push both values directly into the extension's
        storage. Or copy them and paste into the popup manually. Rotating mints a new key and
        invalidates the previous.
      </p>

      <label className="muted" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
        API URL
      </label>
      <CopyableValue value={apiUrl} />

      <label className="muted" style={{ display: "block", marginTop: 12, fontSize: 12 }}>
        Extension API key
      </label>
      {q.isLoading && <p className="muted">Loading…</p>}
      {q.data && currentKey && <CopyableValue value={currentKey} />}
      {q.data && !currentKey && (
        <p className="muted" style={{ fontSize: 12 }}>
          No key yet — click Generate to mint one.
        </p>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button onClick={() => rotate.mutate()} disabled={rotate.isPending}>
          {rotate.isPending ? "Rotating…" : currentKey ? "Rotate key" : "Generate key"}
        </button>
        <button
          onClick={sendToExtension}
          disabled={!currentKey}
          style={{ background: "var(--good)" }}
        >
          Send to extension
        </button>
        {sendStatus && (
          <span
            className={sendStatus.startsWith("✓") ? "tag-active" : sendStatus.startsWith("Failed") || sendStatus.startsWith("No response") ? "tag-error" : "muted"}
            style={{ fontSize: 12 }}
          >
            {sendStatus}
          </span>
        )}
      </div>
    </div>
  );
}

function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop — user can still select */
    }
  }
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "stretch", marginTop: 4 }}>
      <pre
        style={{
          background: "var(--bg)",
          border: "1px solid var(--border)",
          padding: "8px 10px",
          borderRadius: 6,
          userSelect: "all",
          flex: 1,
          margin: 0,
          fontSize: 12,
          overflow: "auto",
        }}
      >
        {value}
      </pre>
      <button onClick={copy} style={{ flexShrink: 0 }}>
        {copied ? "✓ Copied" : "Copy"}
      </button>
    </div>
  );
}
